import type { CityConfig } from "@/pages/city-template/types";

export const coosBayConfig: CityConfig = {
  city: "Coos Bay",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "coos-bay",
  route: "/cities/coos-bay",
  title: "Emergency Passport Coos Bay OR | Expedited Passport Coos County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Coos Bay, OR — as fast as 24 hours. Fast Passport Center serves Coos Bay, North Bend, Bandon, and the Oregon Coast. Registered State Dept. courier. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Coos Bay, Oregon",
  heroSubtitle: "Coos Bay resident with an urgent passport need? As the largest city on Oregon's southern coast, your passport resources are limited — and the nearest regional agency is in Seattle, a 6-hour drive. Fast Passport Center gets Coos Bay residents expedited passports in days, entirely remotely. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Coos%20Bay%20Oregon%20dramatic%20coastal%20landscape%20with%20the%20Coos%20Bay%20harbor%20and%20fishing%20boats%2C%20rugged%20Oregon%20Coast%20cliffs%20with%20crashing%20Pacific%20Ocean%20waves%2C%20Cape%20Arago%20lighthouse%20and%20sea%20stacks%2C%20dramatic%20stormy%20clouds%20with%20golden%20sunlight%20breaking%20through%2C%20lush%20coastal%20temperate%20rainforest%2C%20Pacific%20Northwest%20maritime%20beauty%2C%20photorealistic%20coastal%20scenery%2C%20no%20text%20no%20people&width=1800&height=700&seq=coos-bay-hero-001&orientation=landscape",
  heroImageAlt: "Coos Bay Oregon harbor and Oregon Coast with dramatic Pacific Ocean cliffs",
  geoPosition: "43.3665;-124.2179",
  geoPlacename: "Coos Bay, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 360 miles north — approximately 6 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Coos Bay", zips: "97420" },
    { name: "North Bend", zips: "97459" },
    { name: "Bandon", zips: "97411" },
    { name: "Coquille", zips: "97423" },
    { name: "Myrtle Point", zips: "97458" },
    { name: "Lakeside", zips: "97449" },
    { name: "Reedsport", zips: "97467" },
    { name: "Winchester Bay", zips: "97467" },
    { name: "Florence", zips: "97439" },
    { name: "Roseburg", zips: "97470–97471" },
    { name: "Grants Pass", zips: "97526, 97527" },
    { name: "Medford", zips: "97501–97504" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Coos Bay", desc: "Complete your emergency or expedited passport application from Coos Bay — no drive to Eugene, Portland, or Seattle required." },
    { icon: "ri-truck-line", title: "Ship Overnight from Coos Bay or North Bend", desc: "FedEx and UPS locations in Coos Bay and North Bend serve the southern Oregon Coast for overnight passport document shipping." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — especially important for coastal Oregon residents far from passport resources." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We submit through reserved Seattle Passport Agency channels — bypassing the public appointment system entirely." },
  ],
  testimonials: [
    {
      name: "Michael R.",
      location: "Coos Bay, OR",
      rating: 5,
      text: "Alaska cruise departing from Seattle in three weeks — discovered my passport expired by two months. Fast Passport Center renewed it in 5 days. Coos Bay is hours from any passport office. This remote service was exactly what I needed.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Karen S.",
      location: "North Bend, OR",
      rating: 5,
      text: "My daughter's first passport for a family trip to Hawaii. The Coos Bay Post Office had limited appointments. Fast Passport Center guided us through the North Bend step and expedited it. Passport back in 9 days.",
      service: "First-Time Passport",
    },
    {
      name: "David T.",
      location: "Coos Bay, OR",
      rating: 5,
      text: "Commercial fishing captain — needed my passport renewed for a season working in BC waters. Fast Passport Center handled it in 7 days while I was still prepping the boat. Shipped from the Coos Bay UPS. Couldn't have been smoother.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "coos-bay-post-main",
      name: "Coos Bay Main Post Office",
      type: "USPS Passport Acceptance",
      address: "750 Commercial Ave",
      city: "Coos Bay",
      state: "OR",
      zip: "97420",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Coos Bay — primary passport acceptance facility for the southern Oregon Coast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance for Coos Bay, North Bend, and the southern Oregon Coast. Appointment required — limited availability, especially during summer cruise season.",
      icon: "ri-mail-line",
    },
    {
      id: "coos-bay-post-2",
      name: "Coos Bay North Post Office",
      type: "USPS Passport Acceptance",
      address: "2000 Ocean Blvd SE",
      city: "Coos Bay",
      state: "OR",
      zip: "97420",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "North Coos Bay — secondary option for Coos Bay area residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Coos Bay post office — alternate when the main Coos Bay post office is booked. Limited availability.",
      icon: "ri-mail-line",
    },
    {
      id: "north-bend-post",
      name: "North Bend Post Office",
      type: "USPS Passport Acceptance",
      address: "1301 Virginia Ave",
      city: "North Bend",
      state: "OR",
      zip: "97459",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "North Bend — about 2 miles north of Coos Bay",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Bend post office serves North Bend and north Coos Bay area residents. Often has better availability than the Coos Bay main office.",
      icon: "ri-mail-line",
    },
    {
      id: "bandon-post",
      name: "Bandon Post Office",
      type: "USPS Passport Acceptance",
      address: "375 2nd St SE",
      city: "Bandon",
      state: "OR",
      zip: "97411",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Bandon — about 25 miles south of Coos Bay on US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Bandon post office for Bandon and south Coos County residents. Limited availability — Coos Bay or North Bend preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "coquille-post",
      name: "Coquille Post Office",
      type: "USPS Passport Acceptance",
      address: "55 N Birch St",
      city: "Coquille",
      state: "OR",
      zip: "97423",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Coquille — about 15 miles east of Coos Bay on OR-42",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Coquille post office for inland Coos County residents. Very limited availability.",
      icon: "ri-mail-line",
    },
    {
      id: "myrtle-point-post",
      name: "Myrtle Point Post Office",
      type: "USPS Passport Acceptance",
      address: "424 5th St",
      city: "Myrtle Point",
      state: "OR",
      zip: "97458",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Myrtle Point — about 25 miles east of Coos Bay on OR-42",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Myrtle Point post office for east Coos County residents. Very limited availability — Coos Bay or North Bend strongly preferred.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Coos Bay, Oregon?",
      a: "Yes. Fast Passport Center provides emergency passport processing for Coos Bay residents — as fast as 24 hours. The nearest regional passport agency is in Seattle (6 hours north). We serve Coos Bay entirely remotely: start online, ship overnight from Coos Bay or North Bend, and we handle agency submission.",
    },
    {
      q: "How fast can I renew my passport from Coos Bay, OR?",
      a: "Adult passport renewal from Coos Bay is available in as little as 3 business days through Fast Passport Center. Most adult renewals are by mail — no in-person appointment required. Ship overnight from Coos Bay or North Bend.",
    },
    {
      q: "Where is the best passport acceptance office near Coos Bay?",
      a: "Coos Bay has the Main Post Office (750 Commercial Ave) as the primary local acceptance facility. The North Bend Post Office (2 miles north) often has better availability. The nearest regional passport agency is in Seattle.",
    },
    {
      q: "Does Coos Bay, Oregon have a passport agency?",
      a: "No. Coos Bay has post offices that accept passport applications. The nearest regional passport agency is in Seattle, 6 hours away. Fast Passport Center bypasses the need for a Seattle trip through reserved agency submission.",
    },
    {
      q: "Can I expedite a passport for an Alaska cruise from Coos Bay?",
      a: "Yes. Alaska cruises are among the most popular international trips for Coos Bay residents — departing from Seattle, San Francisco, or Vancouver. Fast Passport Center handles expedited passport renewals in 3–9 business days. Cruises with Canadian ports require a passport.",
    },
    {
      q: "What airport do Coos Bay residents use for international travel?",
      a: "Coos Bay residents typically drive to Portland International Airport (PDX, about 260 miles north via I-5 and OR-42) or Eugene Airport (EUG, about 160 miles east via OR-42) for international flights. North Bend Airport (OTH) has limited regional connections.",
    },
    {
      q: "Can Coos Bay fishing and maritime workers get expedited passports?",
      a: "Yes. Coos Bay is a major commercial fishing port — and many maritime workers travel to Canada (BC), Alaska, and international waters for work. Fast Passport Center handles expedited passports for maritime professionals with tight travel schedules.",
    },
    {
      q: "How long does standard passport processing take from Coos Bay?",
      a: "Standard USPS mail processing from Coos Bay runs 6–13 weeks, and longer during peak summer travel and cruise season. If your trip is within 10 weeks, expedited service through Fast Passport Center is strongly recommended.",
    },
    {
      q: "Can I get a passport without visiting an office in Coos Bay?",
      a: "Yes — most adult Coos Bay residents renewing a passport can do so entirely by mail. Start online with Fast Passport Center, ship overnight from Coos Bay or North Bend, and we handle the rest. No office visit required.",
    },
    {
      q: "Can Coos Bay residents get a passport for a Canada fishing trip?",
      a: "Yes. British Columbia fishing trips are common for Coos Bay residents — especially for salmon and steelhead fishing. A valid passport is required for international travel. Fast Passport Center handles expedited renewals in 3–9 business days.",
    },
  ],
  photoLocations: [
    { name: "Walmart — Coos Bay", address: "100 E Virginia Ave, Coos Bay, OR 97420", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "Rite Aid — Coos Bay", address: "1000 S 1st St, Coos Bay, OR 97420", hours: "Daily 8 AM – 10 PM", website: "https://www.riteaid.com/photo" },
    { name: "Walgreens — Coos Bay", address: "250 W Johnson Ave, Coos Bay, OR 97420", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/photo" },
    { name: "Walmart — North Bend", address: "2000 Newmark Ave, North Bend, OR 97459", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "Walmart — Roseburg", address: "2150 NW Stewart Pkwy, Roseburg, OR 97471", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "Walmart — Eugene", address: "4550 W 11th Ave, Eugene, OR 97402", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "FedEx Office — Eugene", address: "199 E 5th Ave, Eugene, OR 97401", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM", website: "https://www.office.fedex.com/" },
    { name: "Walgreens — Eugene", address: "1740 W 11th Ave, Eugene, OR 97402", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/photo" },
    { name: "Walmart — Grants Pass", address: "135 Grants Pass Pkwy, Grants Pass, OR 97527", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/photo" },
    { name: "FedEx Office — Medford", address: "924 N Central Ave, Medford, OR 97501", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM", website: "https://www.office.fedex.com/" },
  ],
  dmvLocations: [
    {
      name: "Coos Bay Driver & Motor Vehicle Services",
      address: "1000 Central Ave, Coos Bay, OR 97420",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Coos Bay DMV — driver licenses, REAL ID, and ID cards for Coos Bay and North Bend area residents.",
      featured: true,
    },
    {
      name: "Bandon Driver & Motor Vehicle Services",
      address: "375 2nd St SE, Bandon, OR 97411",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Bandon DMV — serves Bandon and south Coos County residents.",
      featured: false,
    },
    {
      name: "Coquille Driver & Motor Vehicle Services",
      address: "55 N Birch St, Coquille, OR 97423",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Coquille DMV — serves inland Coos County including Coquille and Myrtle Point.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "OTH",
      name: "Southwest Oregon Regional Airport",
      distance: "Primary regional airport — North Bend, about 2 miles north of Coos Bay",
      address: "1100 Airport Ln, North Bend, OR 97459",
      note: "Southwest Oregon Regional Airport (OTH) serves Coos Bay and North Bend with regional connections to Portland, San Francisco, and Seattle for onward international flights.",
      flightUrl: "https://www.flyoth.com/",
      flightLabel: "OTH Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "About 260 miles north via OR-42 and I-5",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international hub for Coos Bay residents. PDX offers direct service to Amsterdam, London, Tokyo, Reykjavik, Mexico City, and Vancouver BC.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "About 160 miles east via OR-42",
      address: "28855 Lockheed Dr, Eugene, OR 97402",
      note: "Eugene Airport provides regional connections for Coos Bay residents — closer than PDX for some domestic flights.",
      flightUrl: "https://www.eugene-or.gov/airport",
      flightLabel: "Eugene Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Coos Bay & Oregon Coast",
      description: "Last-minute Alaska cruise? Fishing trip to BC? Family emergency abroad? Fast Passport Center handles emergency passport processing for Coos Bay residents — as fast as 24 hours. No 6-hour Seattle drive required.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal — Coos Bay, OR",
      description: "Passport expired during coastal living? Coos Bay residents renew fast through Fast Passport Center — ship overnight from Coos Bay, we submit through reserved agency access, and return your passport in days.",
      bullets: ["Fastest renewal option for Coos Bay residents", "Ship overnight from Coos Bay or North Bend", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost or stolen passport in Coos Bay or before a trip? We guide coastal Oregon travelers through DS-64 and expedited replacement — entirely remotely.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Coos Bay Families",
      description: "Coos Bay families heading to Hawaii, Alaska, Mexico, or internationally need child passports. We guide you through the Coos Bay or North Bend acceptance step — you handle the visit, we expedite from there.",
      bullets: ["Both parents typically required to consent", "Coos Bay or North Bend acceptance guidance", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Coos Bay? We guide you through the Coos Bay or North Bend post office step and handle expedited processing after.",
      bullets: ["Step-by-step acceptance guidance", "Photo location guide for Coos Bay area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — submitting through reserved agency channels at prices 30–100% lower than retail expediting services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage, damaged passport replacement, second passport — all available with expedited courier access for Coos Bay area residents.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for frequent travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Coos Bay, Oregon travelers with urgent international departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Coos Bay and Coos County residents needing passports faster than standard USPS timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Coos Bay, Oregon residents — ship overnight from Coos Bay or North Bend, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Coos Bay travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Coos Bay and Oregon Coast families traveling internationally." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Coos Bay, Oregon and the southern Oregon Coast area." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Until the Last Week Before a Cruise or Trip", description: "Coos Bay residents book Alaska cruises and Hawaii trips months in advance — but many forget to check passports until packing. Coastal residents are especially vulnerable since the nearest passport resources are 2+ hours away." },
    { icon: "ri-camera-off-line", title: "Passport Photo Rejected by the Government", description: "Photos from small-town pharmacies often have lighting or background issues. Fast Passport Center reviews photo specifications before submission to prevent costly rejections." },
    { icon: "ri-file-damage-line", title: "Missing Signatures or Form Errors", description: "One missing signature causes weeks of delay. Our pre-submission review catches all errors before they reach the agency." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate or Document Format", description: "We specify the exact document format required for your state of birth — preventing the rejections that are common with first-time applicants from coastal Oregon." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule", description: "Alaska, Canada, Hawaii, Mexico, and most international destinations require 6 months of validity. A passport with 3 months left is effectively expired." },
    { icon: "ri-calendar-close-line", title: "Not Accounting for Coastal Distance to Passport Resources", description: "Coos Bay residents need to plan further ahead than Portland residents. The nearest passport acceptance with good availability is often in Eugene or Roseburg — 2+ hours away. Factor travel time into your planning." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Coos Bay travel date. Complete your secure order in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized checklist with Coos Bay and Oregon Coast area acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Coos Bay or North Bend", description: "Drop off at FedEx or UPS in Coos Bay or North Bend — both serve the southern Oregon Coast with overnight shipping." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no waiting for public appointments or 6-hour drives." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Coos Bay Address", description: "Your completed passport ships directly to your Coos Bay or Coos County address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship from Coos Bay or North Bend overnight",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 6 hours each way",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Not practical for Coos Bay residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer during peak season",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent or tracking",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited Seattle slots, weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (6 hours each way from Coos Bay)", fpc: "Ship overnight from Coos Bay or North Bend" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Coastal Accessibility", agency: "Not designed for Oregon Coast residents", fpc: "Fully remote — no car trip to Seattle" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + travel cost", fpc: "Best price guarantee — no travel cost" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Eugene", route: "/cities/eugene" },
    { name: "Emergency passport help in Roseburg", route: "/cities/roseburg" },
    { name: "Fast passport renewal in Grants Pass", route: "/cities/grants-pass" },
    { name: "Rush passport services in Medford", route: "/cities/medford" },
    { name: "Expedited passports for Oregon travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from FedEx or UPS in Coos Bay or North Bend. No office visit required for most services.",
  fpcOfficeDistance: "Serving Coos Bay and the entire southern Oregon Coast area remotely",
  serviceAreaIntro: "Whether you're in Coos Bay, North Bend, Bandon, Coquille, Myrtle Point, or anywhere on the southern Oregon Coast — Fast Passport Center provides emergency and expedited passport processing without the 6-hour drive to Seattle.",
  nearbyCitiesIntro: "We serve all Coos County zip codes including Coos Bay, North Bend, Bandon, Coquille, Myrtle Point, Lakeside, Reedsport, and Winchester Bay.",
  howItWorksIntro: "Getting an emergency or expedited passport from Coos Bay is easier than a 6-hour drive to Seattle. Most southern Oregon Coast clients complete the process without visiting an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Coos Bay and southern Oregon Coast passport applicants. Our review process prevents all of them.",
  testimonialsIntro: "Real reviews from Coos Bay, North Bend, and southern Oregon Coast travelers who needed their passports fast.",
  faqIntro: "The most common questions Coos Bay and Coos County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Coos County Clerk Vital Records", desc: "Obtain vital records through the Coos County Clerk's office.", url: "https://www.co.coos.or.us/clerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Coos Bay and the Southern Oregon Coast",
    intro: "Coos Bay is the largest city on Oregon's southern coast — a commercial fishing port, maritime hub, and gateway to the Oregon Dunes and Cape Arago. The coastal location creates a unique international travel pattern: Alaska cruises, BC fishing trips, Hawaii vacations, and maritime industry travel that drive passport urgency.",
    cards: [
      {
        icon: "ri-ship-line",
        title: "Alaska Cruises from Seattle & San Francisco",
        subtitle: "Alaska cruises are the most common international travel trigger for Coos Bay residents",
        description: "Alaska cruises are extremely popular with Coos Bay and southern Oregon Coast residents — departing from Seattle, San Francisco, and Vancouver BC. These cruises require a passport for all Canadian ports of call (Victoria, Vancouver, Ketchikan, Juneau, Skagway). Cruise bookings happen in January–March for June–August departures. Coos Bay residents often discover expired passports during spring cruise preparation.",
        tip: "Alaska cruise booked? Check your passport the day you book. If it expires before 6 months after your return date, renew immediately. Fast Passport Center handles expedited renewals in 3–9 days from Coos Bay.",
      },
      {
        icon: "ri-drop-line",
        title: "Commercial Fishing & Maritime Industry Travel",
        subtitle: "Coos Bay is a major commercial fishing port with BC and Alaska waters access",
        description: "Coos Bay is one of Oregon's largest commercial fishing ports — with vessels that travel to British Columbia, Alaska, and international waters. Maritime workers need passports for Canadian fishing grounds, international ports, and crew travel. Fishing seasons have strict start dates — a delayed passport can mean a missed season.",
        tip: "Commercial fishing captain or crew member? Check your passport before the season starts. BC salmon season and Alaskan crab season have hard start dates. Expedited processing in 3–9 days is available for maritime professionals.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Southwest Oregon Regional Airport (OTH) & PDX Access",
        subtitle: "OTH in North Bend for regional connections; PDX for direct international flights",
        description: "Southwest Oregon Regional Airport (OTH) in North Bend provides regional connections to Portland, San Francisco, and Seattle. For direct international flights, Portland International (PDX) is 260 miles north via OR-42 and I-5 — about a 4.5-hour drive. Coos Bay residents planning Hawaii, Mexico, or international trips from PDX need valid passports.",
        tip: "If driving to PDX for international travel, check your passport when booking. The 4.5-hour drive from Coos Bay to PDX adds to your pre-departure planning — and you don't want an expired passport to add a 6-hour Seattle trip on top.",
      },
      {
        icon: "ri-sun-cloudy-line",
        title: "Hawaii, Mexico & Winter Warm-Weather Escapes",
        subtitle: "Coos Bay residents escape coastal winter for Hawaii, Mexico, and warm destinations November through April",
        description: "Coos Bay winters are cold, wet, and windy — making Hawaii, Mexico, and Central America popular winter destinations. Puerto Vallarta, Cabo, the Hawaiian Islands, and Costa Rica are common bookings from Coos Bay. These trips are booked in September–October, when passport expiration dates are often discovered for the first time.",
        tip: "Book your winter Hawaii or Mexico trip in September, then check your passport immediately. If it expires before May the following year, renew it now. Fast Passport Center expedites renewals in 3–9 business days.",
      },
    ],
    callout: {
      icon: "ri-landscape-line",
      title: "Coos Bay, Oregon — The Southern Oregon Coast's Maritime Gateway",
      description: "Coos Bay sits at the heart of Oregon's southern coast — a commercial fishing powerhouse, port city, and gateway to the Oregon Dunes. The maritime economy, Alaska cruise culture, and coastal winter escape travel create a unique international travel profile. Fast Passport Center serves Coos Bay, North Bend, Bandon, and all Coos County residents with the same expedited passport expertise as Portland residents — without the Seattle drive.",
    },
  },
  destinations: {
    title: "Where Coos Bay Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations we see most from Coos Bay and southern Oregon Coast clients. The combination of maritime industry, Alaska cruises, coastal winter escapes, and BC fishing creates a distinctive passport urgency profile.",
    stats: [
      { stat: "360 mi", label: "to Seattle Passport Agency — nearest regional office from Coos Bay" },
      { stat: "260 mi", label: "to Portland International Airport (PDX) — primary hub for Coos Bay" },
      { stat: "2 mi", label: "to Southwest Oregon Regional Airport (OTH) — regional connection hub" },
      { stat: "2–9 Days", label: "fastest expedited passport processing available for Coos Bay residents" },
    ],
    destinations: [
      { name: "Alaska — Inside Passage Cruises", context: "Alaska cruises from Seattle are the most popular international travel for Coos Bay residents — passports required for Canadian ports", urgency: "January–March cruise bookings; passport issues discovered at spring cruise check-in", icon: "ri-ship-line" },
      { name: "British Columbia, Canada", context: "BC fishing, Victoria, and Vancouver — accessible by sea and air for Coos Bay maritime and outdoor community", urgency: "Fishing season start dates; spring/summer BC bookings; passport issues discovered at season prep", icon: "ri-map-pin-2-line" },
      { name: "Hawaii — Islands", context: "Winter escape travel for Coos Bay families — warm-weather destination November through April", urgency: "September–October Hawaii bookings; passports found expired during departure prep", icon: "ri-sun-cloudy-line" },
      { name: "Mexico — Cabo, Puerto Vallarta", context: "Winter beach travel for southern Oregon Coast residents — warm-weather escapes from November through April", urgency: "Fall Mexico bookings; passport expiration discovered at booking time", icon: "ri-sun-cloudy-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel for Oregon Coast outdoor community — dry season December through April", urgency: "Fall bookings for winter travel; passport issues discovered at booking", icon: "ri-leaf-line" },
      { name: "Japan & Pacific Rim", context: "Maritime industry connections, Pacific trade, and cultural travel for Coos Bay's international maritime community", urgency: "Trade and industry travel with fixed dates; passport issues discovered at travel confirmation", icon: "ri-global-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Oregon Coast and southern Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Eugene", route: "/cities/eugene" },
      { name: "Roseburg", route: "/cities/roseburg" },
      { name: "Grants Pass", route: "/cities/grants-pass" },
    ],
  },
};